import { Component, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ToastMessageComponent } from 'src/app/shared/components/toast-message/toast-message.component';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SiteService } from 'src/app/shared/services/site.service';
import { CommonService } from 'src/app/services/common.service';
import { FeatureToggleService } from 'src/app/services/feature-toggle.service';
import { ShortcutCode, ShortcutCodePrefix, ShortcutKey } from './services/keyboard-shortcut.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent {
  title = 'site-sequencer';
  // Shared message type used by the host portal and iframe apps for shortcut relays.
  private readonly keyboardShortcutMessageType = '75F_KEYBOARD_SHORTCUT';
  // Prevents a shortcut replayed from the parent from being immediately posted back to the parent.
  private suppressNextParentShortcutRelay = false;
  chatSessionActive: boolean = false;
  chatEnabled: boolean = false;
  /**
   * Tracks whether keyboard tab navigation focus is active in the parent shell (topbar/sidebar).
   * Used to know whether a click inside this iframe needs to clear the parent outline.
   */
  isTabNavActive: boolean = false;

  constructor(
    private router: Router,
    private locaStorageService: LocalStorageService,
    private siteService: SiteService,
    private commonService: CommonService,
    private featureToggleService: FeatureToggleService,
    private toastMessage: MatSnackBar) {
    const currentUrl = new URL(window.location.href);
    const { searchParams } = currentUrl;
    const rawChatEnabled = (searchParams.get('chatEnabled'))?.toLowerCase();
    this.chatEnabled = rawChatEnabled === 'true';
    // Router's initial navigation is disabled (see AppRoutingModule) so that
    // routed components don't read stale LocalStorageService values before
    // the token/user details finish persisting.
    this.handleQueryParams(searchParams).finally(() => this.router.initialNavigation());
  }

  // Toast Message
  showToast(type: string, message: string) {
    this.toastMessage.openFromComponent(ToastMessageComponent, {
      duration: 5000,
      panelClass: 'toast-snackbar-panel',
      data: {
        type: type,
        message: message
      },
    });
  }

  async handleQueryParams(params: URLSearchParams) {
    if (params.has('token')) {
      const token = params.get('token');
      this.locaStorageService.persistToken(token);
      try {
        let userDetails = await lastValueFrom(this.siteService.getUser());
        this.commonService.userDetails = userDetails;
        this.locaStorageService.persistUserId(userDetails.userId);
        this.locaStorageService.persistUserEmail(userDetails.emailId);
      } catch (error: any) {
        this.showToast('error', `${error.message}`);
      }
      this.featureToggleService.devCycleInit();
    }
    if (params.has('userId')) {
      const userId = params.get('userId')
      this.locaStorageService.persistUserId(userId);
    }
    if (params.has('portal')) {
      const portal = params.get('portal') || '';
      sessionStorage.setItem("portal", portal);
    }
    if (params.has('siteName')) {
      const siteNameFromFacPortal = params.get('siteName');
      sessionStorage.setItem("siteNameFromFacPortal", JSON.stringify(siteNameFromFacPortal));
    } else {
      sessionStorage.removeItem('siteNameFromFacPortal');
    }
    if (params.has('selectedSiteId')) {
      sessionStorage.setItem('selectedSiteId', params.get('selectedSiteId') || '');
    } else {
      sessionStorage.removeItem('selectedSiteId');
    }
    if (params.has('remindedSubscriptionSites')) {
      const remindedSubscriptionSites = params.get('remindedSubscriptionSites') ?? '[]';
      const parsedSites: string[] = JSON.parse(remindedSubscriptionSites);
      // clearing the session storage before setting the new sites
      sessionStorage.removeItem('remindedSubscriptionSites');
      if (parsedSites?.length) {
        sessionStorage.setItem('remindedSubscriptionSites', JSON.stringify(parsedSites));
      }
    }
  }

    //Change triggered from parent componet(Internal/Facilisight)
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    let messageFromParent: any = event?.data;
    if (typeof event?.data === 'string') {
      try {
        messageFromParent = JSON.parse(event.data);
      } catch {
        return;
      }
    }

    //Handle the chatSessionActive state(we are swtiching the icons absed on this)
    if (messageFromParent?.chatSessionActive !== undefined) {
      this.chatSessionActive = messageFromParent.chatSessionActive;
    }

    // Synchronize tab navigation state from parent shell (indicates whether topbar/sidebar outline is visible)
    if (messageFromParent?.type === '75F_TAB_NAV_STATE') {
      this.isTabNavActive = !!messageFromParent.active;
    }

    // Parent focus -> iframe shortcut: replay parent-owned messages as local keydown events.
    if (messageFromParent?.type === this.keyboardShortcutMessageType && messageFromParent?.source === 'PARENT') {
      this.replayShortcutFromParent(messageFromParent);
    }
  }

  /**
   * Clears the tab navigation outline on the parent topbar/sidebar when the user clicks inside the iframe.
   * Only posts a message when `isTabNavActive` is true to avoid unnecessary postMessage traffic on every click.
   */
  @HostListener('window:mousedown')
  onMousedown(): void {
    if (this.isTabNavActive) {
      this.isTabNavActive = false;
      window.parent.postMessage(JSON.stringify({ type: '75F_IFRAME_MOUSEDOWN' }), '*');
    }
  }

  //The icon is 
  onToggleChat() {
    window.parent.postMessage(JSON.stringify({chatSessionActive:this.chatSessionActive}), '*');
  }

  /**
   * Intercepts keyboard navigation and shortcut events within the iframe and forwards them to the parent shell.
   * - Tab / Shift+Tab: Allows keyboard focus to cycle between the embedded iframe and the host shell (topbar/sidebar).
   * - Escape: Closes active host panels/drawers or clears focus.
   * - ? / Shift+/: Opens the keyboard shortcut help dialog in the host portal.
   * - S: Opens Saffron AI in the host portal.
   *
   * Form inputs, textareas, and contenteditable elements are bypassed to allow normal typing.
   */
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Do not echo the synthetic keydown that was created from a parent shortcut message.
    if (this.suppressNextParentShortcutRelay) {
      this.suppressNextParentShortcutRelay = false;
      return;
    }

    const target = event.target as HTMLElement;
    const isInput = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    // Do not intercept if user is typing inside a text box or form input
    if (isInput) {
      return;
    }

    const isTab = event.key === ShortcutKey.TAB || event.code === ShortcutCode.TAB;
    const isEscape = event.key === ShortcutKey.ESCAPE || event.code === ShortcutCode.ESCAPE;
    const isHelp = event.key === ShortcutKey.QUESTION_MARK || (event.shiftKey && (event.key === ShortcutKey.SLASH || event.code === ShortcutCode.SLASH));
    const isChatShortcut = event.key.toLowerCase() === 's' && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey;

    // Iframe focus -> parent shortcut: forward host-owned navigation/help shortcuts.
    if (isTab || isEscape || isHelp) {
      event.preventDefault();
      this.postShortcutToParent(event);
    } else if (isChatShortcut) {
// Saffron/chat is owned by the host shell, so send the shortcut to parent instead of handling it here.
      if (this.chatEnabled && !this.chatSessionActive && !this.isAnyDialogOpen()) {
        event.preventDefault();
        this.postShortcutToParent(event);
      }
    } else if (this.shouldBridgeKeyboardEvent(event)) {
      // Forward other app shortcut candidates; parent will ignore anything it does not own.
      this.postShortcutToParent(event);
    }
  }

  private isAnyDialogOpen(): boolean {
    return !!document.querySelector('.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-dialog-container');
  }

  // Replays a parent shortcut inside Site Sequencer so existing local shortcut services can handle it normally.
  private replayShortcutFromParent(payload: any): void {
    this.suppressNextParentShortcutRelay = true;
    document.dispatchEvent(new KeyboardEvent('keydown', {
      key: payload.key,
      code: payload.code,
      shiftKey: !!payload.shiftKey,
      altKey: !!payload.altKey,
      ctrlKey: !!payload.ctrlKey,
      metaKey: !!payload.metaKey,
      bubbles: true,
      cancelable: true
    }));
  }

  // Sends an iframe keydown to the parent shell using the shared shortcut bridge message format.
  private postShortcutToParent(event: KeyboardEvent): void {
    if (window.parent === window) {
      return;
    }

    window.parent.postMessage(JSON.stringify({
      type: this.keyboardShortcutMessageType,
      key: event.key,
      code: event.code,
      shiftKey: !!event.shiftKey,
      altKey: !!event.altKey,
      ctrlKey: !!event.ctrlKey,
      metaKey: !!event.metaKey,
      plain: !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey,
      source: 'IFRAME',
      sequence: `IFRAME-${Date.now()}-${Math.random()}`
    }), '*');
  }

  // Keeps normal typing and browser-reserved shortcuts out of the iframe bridge.
  private shouldBridgeKeyboardEvent(event: KeyboardEvent): boolean {
    if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) {
      return false;
    }

    // Relay app shortcut candidates only: navigation keys plus plain letters/digits.
    return event.key === ShortcutKey.ESCAPE
      || event.key === ShortcutKey.TAB
      || event.key === ShortcutKey.QUESTION_MARK
      || event.code === ShortcutCode.SLASH
      || event.code?.startsWith(ShortcutCodePrefix.KEY)
      || event.code?.startsWith(ShortcutCodePrefix.DIGIT);
  }
}
